import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, useLocation } from "react-router-dom";
import { LoadingIndicator } from "./components/atoms/LoadingIndicator";
import { MenuDetailPopup } from "./components/organisms/menu/MenuDetailPopup";
import { AppCouponModal } from "./components/organisms/modal/AppCouponModal";
import { ContactUsModal } from "./components/organisms/modal/ContactUsModal";
import { LanguageMessageModal } from "./components/organisms/modal/LanguageMessageModal";
import { LanguageSelectModal } from "./components/organisms/modal/LanguageSelectModal";
import { PopupModal } from "./components/organisms/modal/PopupModal";
import { StoreDetailPopup } from "./components/organisms/store/StoreDetailPopup";
import { AppCoupon } from "./components/pages/app_coupon";
import { AppCouponList } from "./components/pages/app_coupon_list";
import { AppLinkage } from "./components/pages/app_linkage";
import { Campaign } from "./components/pages/campaign";
import { ChangeEmail } from "./components/pages/change_email";
import { ChangeEmailMessage } from "./components/pages/change_email_message";
import { ChangeEmailRegistrationMessage } from "./components/pages/change_email_registration_message";
import { CommercialLaw } from "./components/pages/commercial_law";
import { CustomerInfo } from "./components/pages/customer_info";
import { CustomerInfoAuth } from "./components/pages/customer_info_auth";
import { CustomerInfoConfirmation } from "./components/pages/customer_info_confirmation";
import { CustomerInfoEdit } from "./components/pages/customer_info_edit";
import { Deactivate } from "./components/pages/deactivate";
import { EmailEdit } from "./components/pages/email_edit";
import { FAQ } from "./components/pages/faq";
import { ForgotPassword } from "./components/pages/forgot_password";
import { Home } from "./components/pages/home";
import { LegalForUse } from "./components/pages/legal_for_use";
import { Login } from "./components/pages/login";
import { Maintenance } from "./components/pages/maintenance";
import { MembershipInfoSent } from "./components/pages/membership_info_sent";
import { MenuList } from "./components/pages/menu_list";
import { MyPage } from "./components/pages/my_page";
import { NotFound } from "./components/pages/NotFound";
import { OrderComplete } from "./components/pages/order_complete";
import { OrderConfirmation } from "./components/pages/order_confirmation";
import { OrderHistory } from "./components/pages/order_history";
import { PrivacyPolicy } from "./components/pages/privacy_policy";
import { Registration } from "./components/pages/registration";
import { RegistrationMessage } from "./components/pages/registration_message";
import { ResetPassword } from "./components/pages/reset_password";
import { ResetPasswordMessage } from "./components/pages/reset_password_message";
import { ShopLocationSearch } from "./components/pages/shop_location_search";
import { ShopSearch } from "./components/pages/shop_search";
import { TermOfService } from "./components/pages/term_of_service";
import { UserGuide } from "./components/pages/user_guide";
import { AuthTemplate } from "./components/templates/AuthTemplate";
import { MainTemplate } from "./components/templates/MainTemplate";
import { MaintenanceTemplate } from "./components/templates/MaintenanceTemplate";
import { PageRoutes } from "./constants/routes";
import { appActions, appSelectors } from "./ducks/app";
import { menuSelectors } from "./ducks/menu";
import { useCoupon } from "./hooks/useCoupon";
import { useMaintenance } from "./hooks/useMaintenance";

export const RouteConfig = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const isLoading = useSelector(appSelectors.isLoading);

  const showModal = useSelector(appSelectors.showModal);
  const modalObj = useSelector(appSelectors.modalObject);

  const showContactUsModal = useSelector(appSelectors.showContactUsModal);
  const showLanguageMessageModal = useSelector(
    appSelectors.showLanguageMessageModal,
  );
  const showLanguageSelectModal = useSelector(
    appSelectors.showLanguageSelectModal,
  );

  const showMenuDetailPopup = useSelector(appSelectors.showMenuDetailPopup);
  const menuDetailPopupObject = useSelector(appSelectors.menuDetailPopupObject);

  const showStoreDetailPopup = useSelector(appSelectors.showStoreDetailPopup);
  const storeDetailPopupObject = useSelector(
    appSelectors.storeDetailPopupObject,
  );

  const menusToReorder = useSelector(menuSelectors.menusToReorder);

  const { isAppMaintenance } = useMaintenance();

  const closeModal = useCallback(() => {
    dispatch(appActions.closeModal());
  }, []);

  useEffect(() => {
    if (location.pathname !== PageRoutes.LEGAL_FOR_USE) window.scrollTo(0, 0);
  }, [location]);

  const { appliedCoupons, selectedAppCoupons } = useCoupon();

  const showAppCouponHeader = useMemo(() => {
    const hasAppCoupon = appliedCoupons
      ? appliedCoupons.some((coupon) => coupon.isAppCoupon)
      : false;

    return (
      (selectedAppCoupons !== null && selectedAppCoupons.length > 0) ||
      hasAppCoupon
    );
  }, [selectedAppCoupons, appliedCoupons, location]);

  // Show maintenance page if maintenance is active
  if (isAppMaintenance) {
    return (
      <MaintenanceTemplate>
        <Maintenance />
        <LoadingIndicator isShown={isLoading} />
      </MaintenanceTemplate>
    );
  }

  return (
    <>
      <Routes>
        <Route
          path={PageRoutes.HOME}
          element={
            <MainTemplate
              showAppCouponHeader={showAppCouponHeader}
              menusToReorder={menusToReorder}
            />
          }
        >
          <Route
            index
            element={
              <AuthTemplate>
                <Home showAppCouponHeader={showAppCouponHeader} />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.EARLY_BIRD_CAMPAIGN} element={<MainTemplate />}>
          <Route index element={<Campaign />} />
        </Route>

        <Route
          path={PageRoutes.APP_LINKAGE_COUPON}
          element={<MainTemplate showAppCouponHeader={showAppCouponHeader} />}
        >
          <Route
            index
            element={
              <AuthTemplate>
                <Home showAppCouponHeader={showAppCouponHeader} />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.LOGIN}
          element={<MainTemplate requiredLogout />}
        >
          <Route
            index
            element={
              <AuthTemplate requiredLogout>
                <Login />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.MY_PAGE} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate requiredLogin>
                <MyPage />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.ORDER_HISTORY} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate requiredLogin>
                <OrderHistory />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.USER_GUIDE} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate>
                <UserGuide />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.SHOP_LOCATION_SEARCH}
          element={<MainTemplate />}
        >
          <Route
            index
            element={
              <AuthTemplate>
                <ShopLocationSearch />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.SHOP_SEARCH}
          element={<MainTemplate showAppCouponHeader={showAppCouponHeader} />}
        >
          <Route
            index
            element={
              <AuthTemplate>
                <ShopSearch showAppCouponHeader={showAppCouponHeader} />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.SHOP_SEARCH_2}
          element={<MainTemplate showAppCouponHeader={showAppCouponHeader} />}
        >
          <Route
            index
            element={
              <AuthTemplate>
                <ShopSearch showAppCouponHeader={showAppCouponHeader} />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.MENU_LIST}
          element={<MainTemplate showAppCouponHeader={false} />}
        >
          <Route
            index
            element={
              <AuthTemplate>
                <MenuList showAppCouponHeader={false} />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.ORDER_CONFIRMATION_DEFAULT}
          element={<MainTemplate showAppCouponHeader={false} />}
        >
          <Route
            index
            element={
              <AuthTemplate>
                <Home showAppCouponHeader={false} />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.ORDER_CONFIRMATION}
          element={<MainTemplate showAppCouponHeader={false} />}
        >
          <Route
            index
            element={
              <AuthTemplate requiredLogin redirectToLogin>
                <OrderConfirmation showAppCouponHeader={false} />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.ORDER_COMPLETE} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate requiredLogin>
                <OrderComplete />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.ORDER_COMPLETE_DEFAULT}
          element={<MainTemplate showAppCouponHeader={showAppCouponHeader} />}
        >
          <Route
            index
            element={
              <AuthTemplate>
                <Home showAppCouponHeader={showAppCouponHeader} />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.TERM_OF_SERVICE} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate>
                <TermOfService />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.FAQ} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate>
                <FAQ />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.COMMERCIAL_LAW} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate>
                <CommercialLaw />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.PRIVACY_POLICY} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate>
                <PrivacyPolicy />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.DEACTIVATE} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate requiredLogin>
                <Deactivate />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.RESET_PASSWORD_MESSAGE}
          element={<MainTemplate requiredLogout />}
        >
          <Route
            index
            element={
              <AuthTemplate requiredLogout>
                <ResetPasswordMessage />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.FORGOT_PASSWORD}
          element={<MainTemplate requiredLogout />}
        >
          <Route
            index
            element={
              <AuthTemplate requiredLogout>
                <ForgotPassword />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.REGISTRATION}
          element={<MainTemplate requiredLogout />}
        >
          <Route
            index
            element={
              <AuthTemplate requiredLogout>
                <Registration />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.REGISTRATION_DEFAULT}
          element={<MainTemplate showAppCouponHeader={showAppCouponHeader} />}
        >
          <Route
            index
            element={
              <AuthTemplate>
                <Home showAppCouponHeader={showAppCouponHeader} />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.REGISTRATION_MESSAGE}
          element={<MainTemplate requiredLogout />}
        >
          <Route
            index
            element={
              <AuthTemplate requiredLogout>
                <RegistrationMessage />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.MEMBERSHIP_INFO_SENT}
          element={<MainTemplate requiredLogout />}
        >
          <Route
            index
            element={
              <AuthTemplate requiredLogout>
                <MembershipInfoSent />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.RESET_PASSWORD}
          element={<MainTemplate requiredLogout />}
        >
          <Route
            index
            element={
              <AuthTemplate requiredLogout>
                <ResetPassword />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.RESET_PASSWORD_DEFAULT}
          element={<MainTemplate showAppCouponHeader={showAppCouponHeader} />}
        >
          <Route
            index
            element={
              <AuthTemplate>
                <Home showAppCouponHeader={showAppCouponHeader} />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.CUSTOMER_INFO}
          element={<MainTemplate requiredLogout />}
        >
          <Route
            index
            element={
              <AuthTemplate requiredLogout>
                <CustomerInfo />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.CUSTOMER_INFO_AUTH}
          element={<MainTemplate requiredLogout />}
        >
          <Route
            index
            element={
              <AuthTemplate requiredLogout>
                <CustomerInfoAuth />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.CUSTOMER_INFO_CONFIRMATION}
          element={<MainTemplate requiredLogout />}
        >
          <Route
            index
            element={
              <AuthTemplate requiredLogout>
                <CustomerInfoConfirmation />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.CUSTOMER_INFO_EDIT} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate requiredLogin>
                <CustomerInfoEdit />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.EMAIL_EDIT} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate requiredLogin>
                <EmailEdit />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.CHANGE_EMAIL_MESSAGE}
          element={<MainTemplate />}
        >
          <Route
            index
            element={
              <AuthTemplate requiredLogin>
                <ChangeEmailMessage />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.CHANGE_EMAIL} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate requiredLogin>
                <ChangeEmail />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.CHANGE_EMAIL_DEFAULT}
          element={<MainTemplate showAppCouponHeader={showAppCouponHeader} />}
        >
          <Route
            index
            element={
              <AuthTemplate>
                <Home showAppCouponHeader={showAppCouponHeader} />
              </AuthTemplate>
            }
          />
        </Route>

        <Route
          path={PageRoutes.CHANGE_EMAIL_REGISTRATION_MESSAGE}
          element={<MainTemplate />}
        >
          <Route
            index
            element={
              <AuthTemplate requiredLogin>
                <ChangeEmailRegistrationMessage />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.LEGAL_FOR_USE} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate>
                <LegalForUse />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.LEGAL_FOR_USE_QUERY} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate>
                <LegalForUse />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.APP_COUPON} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate>
                <AppCoupon />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.APP_LINKAGE} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate>
                <AppLinkage />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={PageRoutes.APP_COUPON_LIST} element={<MainTemplate />}>
          <Route
            index
            element={
              <AuthTemplate>
                <AppCouponList />
              </AuthTemplate>
            }
          />
        </Route>

        <Route path={"*"} element={<NotFound />} />
      </Routes>

      <PopupModal
        isOpen={showModal}
        {...modalObj!}
        defaultCloseModal={closeModal}
      />
      <ContactUsModal
        isOpen={showContactUsModal}
        handleClose={() => dispatch(appActions.closeContactUsModal())}
      />
      <LanguageMessageModal
        isOpen={showLanguageMessageModal}
        handleClose={() => dispatch(appActions.closeLanguageMessageModal())}
      />
      <LanguageSelectModal
        isOpen={showLanguageSelectModal}
        handleClose={() => dispatch(appActions.closeLanguageSelectModal())}
      />
      <AppCouponModal />

      <MenuDetailPopup
        isShow={showMenuDetailPopup}
        {...menuDetailPopupObject}
      />

      {showStoreDetailPopup && (
        <StoreDetailPopup
          isShow={showStoreDetailPopup}
          {...storeDetailPopupObject}
        />
      )}

      <LoadingIndicator isShown={isLoading} />
    </>
  );
};
